import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { load } from "../lib/storage";
import { leseMass, schluessel, type KachelMass, type SkelettMass } from "../lib/skelettMass";
import { useStore } from "../store/useStore";

// Drahtgitter-Skelette fuer die Ladephasen.
//
// LEITSATZ DIESER FASSUNG: nicht Masse abschreiben, sondern die ECHTEN
// Bauteile leer bauen.
// Die Vorgaengerin trug eine Tabelle handgemessener Pixelzahlen (1440/1000/
// 700/500/390). Die Hoehen sassen damit auf den Punkt — die FORM nicht:
// gemessen 43 % Deckung, 31 % der gezeichneten Flaeche stand, wo nie etwas kam.
// Die Ursachen waren immer dieselben zwei:
//   (a) gefuellte Rechtecke fuer durchsichtige Bedienelemente. Kopfknoepfe,
//       Filterchips und Reiter sind Umriss plus Text; das Skelett fuellte die
//       ganze Flaeche und malte damit das Drei- bis Vierfache an Tinte.
//   (b) EINE Riesenkarte fuer den Diagrammbereich, wo die Seite ein Raster aus
//       drei bis fuenf Kacheln stellt (334 306 px² Fehlflaeche — der groesste
//       Einzelposten der ganzen Messung).
// Deshalb steht hier jetzt so weit wie moeglich dieselbe Klasse wie in der
// fertigen Seite (.card, .hero-grid/.hero-tile, .filterbar-row/.filterbar-chips,
// .dash-grid/.dash-tile, .btn/.btn-ghost, .filterws-open), und die Balken
// stehen INNERHALB dieser Bauteile. Polster, Rundung, Umbruchpunkt und
// Zeilenhoehe kommen damit aus base.css — aus derselben Quelle wie die Seite.
// Was danach noch offen bleibt (wie viele Kacheln? wie lang die Namen?), kann
// eine Silhouette beim Zeichnen nicht wissen — das merkt sich die
// Selbstvermessung (lib/skelettMass.ts) und reicht es beim naechsten Laden
// herein.
//
// Die Schimmer-Optik kommt aus .skeleton-row in base.css, samt dem
// gestaffelten Glanz (nth-child(7n+k)). Der Versatz greift ueber die POSITION
// unter Geschwistern — er ueberlebt diesen Umbau, weil weiterhin viele
// Balken als Geschwister in unterschiedlichen Positionen stehen.

const SKL_CSS = `
  /* Die Schiene traegt zusaetzlich .psw-rail — Grund, Polster, das Hochziehen
     um 4 px und die Sichtbarkeit kommen damit aus base.css, aus derselben
     Quelle wie bei der echten Schiene. Ohne das sass das ganze Skelett am
     Telefon 4 px zu tief (gemessen 27.08.). Hoehe 38 = 34 Pille + 4 Polster. */
  .skl-rail { display: flex; gap: 6px; height: 38px; margin-bottom: 16px; }
  /* Kennzahlkacheln: KEINE eigenen Masse. Polster, Abstand, Kachelhoehe
     und Schriftmasse kommen aus .hero-grid/.hero-tile/… in base.css — also aus
     derselben Quelle wie die fertige Seite. Hier steht nur, was das Skelett
     zusaetzlich braucht. */
  .skl-kachel-akzent { background: linear-gradient(150deg, var(--accent), var(--accent-2)); border-color: transparent; }
  .skl-kachel-akzent .skeleton-row { background: rgba(255, 255, 255, 0.26); }
  .skl-kachel-akzent .skeleton-row::after { background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.24), transparent); }
  /* Nur mit aktivem Filter sichtbar: die "von"-Zeile und die Bedingungspille */
  .skl-nur-aktiv, .skl-bedingung { display: none; }
  .skl-aktiv .skl-nur-aktiv { display: block; }
  .skl-aktiv span.skl-nur-aktiv { display: inline-block; }
  .skl-aktiv .skl-bedingung { display: flex; gap: 8px; align-items: center; margin-top: 10px; }
  /* Der Aktivbalken der Reiter sitzt wie im Original (primitives.tsx, Tabs):
     8 px innen eingerueckt, 2 px hoch, 1 px unter der Trennlinie. */
  .skl-tab-aktiv::after { content: ""; position: absolute; left: 8px; right: 8px; bottom: -1px; height: 2px; border-radius: 2px; background: var(--accent-text); }
  /* Bedienelemente im Skelett sind Attrappen: sie duerfen weder Zeiger noch
     Tastatur an sich ziehen. */
  .skl-tot { pointer-events: none; }
  /* Objektlisten-Silhouette: Tabelle am Schreibtisch, Kartenzeilen am Telefon —
     derselbe Umbruchpunkt wie die echte Liste. */
  .skl-liste-mobil { display: none; }
  .skl-wz { display: flex; }
  @media (max-width: 640px) {
    .skl-liste-tisch { display: none; }
    .skl-liste-mobil { display: block; }
  }
  /* Zweispalter der Verwaltung. Eigene Klasse statt .admin-grid: deren
     Media Query wohnt im <style> von AdminScreen und ist genau dann NICHT im
     Dokument, wenn das Skelett steht — der Bildschirm ist ja noch nicht
     gemountet. Die Grenze 880 ist von dort uebernommen. */
  .skl-zweispalt { display: grid; grid-template-columns: minmax(0,1fr) minmax(0,1fr); gap: 20px; align-items: start; }
  @media (max-width: 880px) {
    .skl-zweispalt { grid-template-columns: 1fr; }
  }
  @media (max-width: 720px) {
    .karten-skelett-links { display: none !important; }
  }
`;

/* Die Textzeile der Kommandoleiste. Sie steht hier ein zweites Mal, WOERTLICH
   aus app/CommandBar.tsx — bewusst kopiert statt importiert: der Import zoege
   die ganze Kommandoleiste samt ihrer Befehlsliste in das Hauptbuendel, in dem
   dieses Skelett wohnt. Der Preis ist eine Zeichenkette, die auseinanderlaufen
   kann; sichtbar wuerde das als eine um wenige Pixel falsch lange Textzeile im
   Skelett — und nur bis zur ersten Selbstvermessung. */
const SUCH_PLATZHALTER = {
  de: "Suchen oder fragen — Karte, Projekte, Auswertungen, Spielplatz-KI…",
  en: "Search or ask — map, projects, charts, playground AI…",
};

/**
 * Balken in TEXTBREITE.
 *
 * Der Text wird wirklich gesetzt, nur unsichtbar — damit traegt die Schrift
 * selbst die Breite: in jeder Sprache, bei jeder Fenstergroesse und mit
 * derselben Ellipse wie im Original. Eine abgeschriebene Pixelzahl kann das
 * nicht, sie stimmt fuer genau einen Fall (gemessen: Beschriftungsbalken
 * 242 px gegen echte Beschriftungen von 37–63 px).
 * Liegt eine Messung vor (`w`), gewinnt sie — dann ist auch der unbekannte
 * Text (Projektname, Diagrammtitel) auf den Punkt.
 */
function Tb({ txt, w, style, className = "" }: { txt: string; w?: number; style?: CSSProperties; className?: string }) {
  return (
    <span
      className={`skeleton-row ${className}`}
      aria-hidden
      style={{ display: "inline-block", color: "transparent", borderRadius: 5, whiteSpace: "nowrap", maxWidth: "100%", verticalAlign: "middle", ...(w ? { width: w } : null), ...style }}
    >
      {txt}
    </span>
  );
}

/** Quadrat in Symbolgroesse — Icons sind im Original fast immer quadratisch. */
function Ib({ s = 16, r = 5, style }: { s?: number; r?: number; style?: CSSProperties }) {
  return <span className="skeleton-row" aria-hidden style={{ display: "inline-block", width: s, height: s, borderRadius: r, flex: "none", ...style }} />;
}

/**
 * Attrappe eines Knopfes — ECHTES <button>, nur leer und tot.
 *
 * Warum kein <span> mit denselben Klassen: ein Knopf erbt die Zeilenhoehe des
 * Koerpers NICHT (UA-Vorgabe `normal`), ein span schon. Gemessen ist der echte
 * Ghost-Knopf 39 px hoch; als span waere dieselbe Klasse 43 px hoch geworden
 * und die Kopfzeile 4 px zu hoch. Mit dem echten Element kann das nicht
 * passieren — Polster, Rundung und Zeilenhoehe kommen aus derselben Regel.
 */
function Knopf({ children, className = "", style }: { children: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <button type="button" tabIndex={-1} aria-hidden className={`skl-tot ${className}`} style={style}>
      {children}
    </button>
  );
}

const CHART_H = 250; // components/charts.tsx, tileChartHeight() — fuer JEDE Spannweite

/** Rueckfall ohne Messung: drei schmale Kacheln, Hoehe natuerlich (Kopf +
 *  Diagramm + Polster). Absichtlich OHNE Hinweiszeile — lieber etwas zu wenig
 *  Tinte als eine, die nirgends ankommt. */
const KACHELN_RUECKFALL: KachelMass[] = [
  [1, 0, 0],
  [1, 0, 0],
  [1, 0, 0],
];

/**
 * Eine Diagrammkachel als Umriss — derselbe Rahmen wie TileFrame.
 * `h` 0 heisst „nicht gemessen": dann bestimmt der Inhalt die Hoehe, und die
 * ist am Schreibtisch wie am Telefon von selbst richtig.
 */
function KachelSkelett({ span, h, hinweis }: { span: number; h: number; hinweis: number }) {
  return (
    <div
      data-span={span}
      className="card dash-tile"
      style={{ gridColumn: `span ${span}`, padding: "11px 12px 8px", background: "var(--field)", display: "flex", flexDirection: "column", overflow: "hidden", ...(h ? { height: h } : null) }}
    >
      {/* Kopfzeile der Kachel: Ziehgriff, Titel, darunter Etikett und Antwortzahl */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 7, marginBottom: 6, minHeight: 26, flex: "none" }}>
        <Ib s={15} r={4} />
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
          <Tb txt="Spielflächen je Ortsteil" style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.2, maxWidth: "82%" }} />
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Tb txt="bar" style={{ fontSize: 10.5, lineHeight: 1.5 }} />
            <Tb txt="1.234" style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.2 }} />
          </span>
        </div>
      </div>
      {/* Das Diagramm ist ein canvas — eine ruhige Flaeche ist dafuer die
          ehrliche Silhouette, kein Balkenwald. Genau so macht es die Kachel
          im Betrieb auch (ProjectStats, Enthuellung um <EChart>).
          flex:1 statt fester Hoehe: liegt eine gemessene Kachelhoehe vor,
          schluckt das Diagrammfeld die paar Pixel Rest, statt sie als Loch
          am Kachelfuss stehen zu lassen. */}
      <div className="skeleton-row" style={{ flex: 1, minHeight: CHART_H, borderRadius: 12 }} />
      {/* Hinweiszeile („Balken anklicken filtert…") — nur wo die Messung eine
          gesehen hat. Sie ist der Grund, warum eine Kachel im selben Raster
          hoeher ist als ihre Nachbarin. */}
      {hinweis > 0 && (
        <div style={{ height: hinweis, marginTop: 6, flex: "none", display: "flex", flexDirection: "column", gap: 4, overflow: "hidden" }}>
          <span className="skeleton-row" style={{ height: 11, borderRadius: 4 }} />
          <span className="skeleton-row" style={{ height: 11, width: "62%", borderRadius: 4 }} />
        </div>
      )}
    </div>
  );
}

/** Diagramm-Silhouette in Endmass — auch fuer den Reiterinhalt im Dashboard.
 *
 *  Ohne `mass` liest sie die Selbstvermessung selbst nach: die Silhouette
 *  erscheint auch MITTEN in der fertigen Seite (waehrend echarts nachlaedt),
 *  und dort ist die abgelegte Kachelform genauso gueltig wie beim Routenwechsel. */
export function DiagrammSkelett({ mass }: { mass?: SkelettMass | null }) {
  const selbst = useDashMass();
  const m = mass !== undefined ? mass : selbst.mass;
  const kacheln = m?.kacheln?.length ? m.kacheln : KACHELN_RUECKFALL;
  const de = selbst.de;
  return (
    <div aria-busy>
      <style>{SKL_CSS}</style>
      {/* Kopf „Kennzahlen" + Diagramm-Knopf — dieselbe Zeile wie im Original */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Tb txt={de ? "Kennzahlen" : "Metrics"} style={{ fontSize: 17, fontWeight: 600, lineHeight: 1.5, borderRadius: 6 }} />
        </div>
        <Knopf className="btn btn-primary" style={{ padding: "7px 13px", fontSize: 13 }}>
          <Ib s={15} />
          <Tb txt={de ? "Diagramm" : "Chart"} style={{ fontSize: 13, lineHeight: 1.2 }} />
        </Knopf>
      </div>
      {/* DAS Raster — nicht eine Karte. Spannweite und Hoehe je Kachel kommen
          aus der Messung; die Zeilenaufteilung besorgt dann grid-auto-flow
          dense von selbst, genau wie in der fertigen Seite. */}
      <div className="dash-grid">
        {kacheln.map(([span, h, hinweis], i) => (
          <KachelSkelett key={i} span={span} h={h} hinweis={hinweis} />
        ))}
      </div>
    </div>
  );
}

/** Objektlisten-Silhouette: Werkzeugzeile (Suche links, Umschalter rechts),
 *  Tabellenkopf und Zeilen mit Foto-Miniatur — Telefon: Sortierchips + Karten.
 *
 *  Zwei Befunde der Messung stecken hier drin:
 *  — Die echte Tabelle laeuft bis zum unteren Bildrand und darueber hinaus;
 *    sechs feste Zeilen endeten bei y=860 und liessen das untere Fuenftel des
 *    Bildes leer. Die Zeilenzahl richtet sich deshalb nach der Fensterhoehe.
 *  — Die Spalten sassen ZWISCHEN den echten Spalten (Balken bei x=280 und 380,
 *    echte Spalten bei 510/537/936). Sie stehen jetzt an den gemessenen
 *    Anteilen der Tabellenbreite: Nummer rechtsbuendig bis 31 %, Name ab 31 %,
 *    Ortsteil ab 68 %. */
function ListeSkelett() {
  const zeilen = typeof window === "undefined" ? 8 : Math.max(6, Math.ceil((window.innerHeight - 470) / 50));
  return (
    <div aria-busy>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
        <div className="skeleton-row" style={{ height: 36, width: 132, borderRadius: 10, flex: "none" }} />
        <div className="skl-liste-tisch skl-wz" style={{ gap: 8 }}>
          {[108, 84, 96, 118].map((w, i) => (
            <div key={i} className="skeleton-row" style={{ height: 36, width: w, borderRadius: 11 }} />
          ))}
        </div>
        <span style={{ flex: 1 }} />
        <div className="skeleton-row" style={{ height: 36, width: 148, borderRadius: 11, flex: "none" }} />
      </div>
      {/* Tisch: Kopfzeile + Zeilen mit Miniatur. Die Spaltenkaesten sind
          durchsichtig (nur der Balken darin traegt Tinte) — die echte Tabelle
          hat dort ebenfalls nur Text. */}
      <div className="skl-liste-tisch">
        <div style={{ display: "flex", alignItems: "center", padding: "10px 14px 12px 18px" }}>
          <span style={{ width: "31%", display: "flex", justifyContent: "flex-end", paddingRight: 12 }}>
            <span className="skeleton-row" style={{ height: 11, width: 62, borderRadius: 5 }} />
          </span>
          <span style={{ width: "37%" }}>
            <span className="skeleton-row" style={{ display: "inline-block", height: 11, width: 74, borderRadius: 5 }} />
          </span>
          <span style={{ flex: 1 }}>
            <span className="skeleton-row" style={{ display: "inline-block", height: 11, width: 66, borderRadius: 5 }} />
          </span>
        </div>
        {Array.from({ length: zeilen }, (_, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", padding: "8px 14px 8px 18px", borderTop: "1px solid var(--glass-border)" }}>
            <span style={{ width: "31%", display: "flex", alignItems: "center", gap: 14 }}>
              <span className="skeleton-row" style={{ height: 33, width: 44, borderRadius: 8, flex: "none" }} />
              <span style={{ flex: 1 }} />
              <span className="skeleton-row" style={{ height: 13, width: 78, borderRadius: 5, flex: "none", marginRight: 12 }} />
            </span>
            <span style={{ width: "37%" }}>
              <span className="skeleton-row" style={{ display: "inline-block", height: 13, width: "62%", borderRadius: 5 }} />
            </span>
            <span style={{ flex: 1 }}>
              <span className="skeleton-row" style={{ display: "inline-block", height: 13, width: "52%", borderRadius: 5 }} />
            </span>
            <span className="skeleton-row" style={{ height: 14, width: 14, borderRadius: 5, flex: "none" }} />
          </div>
        ))}
      </div>
      {/* Telefon: Sortierchips + Kartenzeilen */}
      <div className="skl-liste-mobil">
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          {[92, 130, 74].map((w, i) => (
            <div key={i} className="skeleton-row" style={{ height: 26, width: w, borderRadius: 9 }} />
          ))}
        </div>
        {Array.from({ length: zeilen }, (_, i) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", padding: "9px 2px", borderTop: "1px solid var(--glass-border)" }}>
            <div className="skeleton-row" style={{ height: 38, width: 50, borderRadius: 9, flex: "none" }} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              <div className="skeleton-row" style={{ height: 13, width: "55%", borderRadius: 5 }} />
              <div className="skeleton-row" style={{ height: 10, width: "38%", borderRadius: 4 }} />
            </div>
            <div className="skeleton-row" style={{ height: 20, width: 46, borderRadius: 7, flex: "none" }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Alles, was die Dashboard-Silhouette ueber DIESES Laden schon weiss.
 *
 * Zwei Quellen, beide ohne Serverrunde: der persistierte Store (Anmeldung,
 * gemerkter Filter, zuletzt offener Reiter) und die abgelegte Selbstvermessung.
 * Als Hook, weil beide Silhouetten (ganze Seite und Diagrammblock) dasselbe
 * brauchen — und ein Hook zweimal aufgerufen ist billiger als zwei Kopien,
 * die auseinanderlaufen.
 */
function useDashMass() {
  const dashFilter = useStore((s) => s.dashFilter);
  const lastTab = useStore((s) => s.lastDashTab);
  const kennung = useStore((st) => st.auth?.id ?? st.auth?.username ?? "anon");
  const istAdmin = useStore((st) => st.auth?.role === "admin");
  const de = useStore((s) => s.lang) === "de";
  const projektId =
    typeof window !== "undefined"
      ? (window.location.pathname.match(/\/project\/([^/]+)/)?.[1] ?? new URLSearchParams(window.location.search).get("project") ?? "")
      : "";
  const filterAktiv = !!projektId && (dashFilter[projektId]?.clauses?.length ?? 0) > 0;
  /* Vorgabe FALSCH herum waere teurer: ohne lesbaren Speicher (privates
     Fenster, gesperrte Website-Daten) ist JEDER Aufruf ein erster. Eine
     vorgezeichnete Schiene, die es nicht gibt, kostet dann bei jedem Laden
     50 px Sprung; eine fehlende Schiene kostet ihn nur den Konten mit
     mehreren Projekten, und dort auch nur beim allerersten Mal. */
  const railDa = load<boolean>(`skelettRail.${kennung}`, false);
  const tabListe = !!projektId && lastTab[projektId] === "liste";
  /* Gemessene Masse der ECHTEN Seite — je Breitenband, Filterzustand und
     Projekt. Der Horcher auf die Bandgrenzen ist noetig, weil die Masse
     inline gesetzt werden und eine Media Query sie dann nicht mehr korrigiert
     (Drehen des Telefons waehrend des Ladens). */
  const [, neuMessen] = useState(0);
  useEffect(() => {
    const mqs = [340, 400, 430, 560, 580, 760, 768, 880].map((g) => window.matchMedia(`(max-width: ${g}px)`));
    const h = () => neuMessen((n) => n + 1);
    mqs.forEach((m) => m.addEventListener("change", h));
    return () => mqs.forEach((m) => m.removeEventListener("change", h));
  }, []);
  const mass: SkelettMass | null = projektId ? leseMass(schluessel(projektId, filterAktiv, railDa)) : null;
  return { mass, filterAktiv, railDa, tabListe, istAdmin, de };
}

/** Ganze Dashboard-Seite als Drahtgitter — Routen-Fallback und Projekt-
 *  Ladephase zeigen dieselbe Geometrie wie die fertige Seite.
 *
 *  OHNE ANMELDUNG ZEIGT ES NICHTS. Die Silhouette ist ein Versprechen ("dein
 *  Dashboard entsteht gerade") — wer nicht angemeldet ist, bekommt gleich die
 *  Anmeldeseite, und ihm vorher Kennzahlkacheln vorzuzeichnen waere eine
 *  Zusage, die die naechste Sekunde bricht. Heute schuetzen alle Einbauorte
 *  sich selbst (RequireAuth/RequireAdmin rendern ihr Navigate, BEVOR das
 *  faule Kind suspenden kann; HomeRedirect prueft auth vor dem Skelett) —
 *  aber diese Zusicherung lebt verstreut in den Routen, und EIN kuenftiger
 *  Einbauort ohne Waechter hebelte sie still aus. Darum steht sie zusaetzlich
 *  HIER, am Skelett selbst: auth === null → null. Nichts ist in dem Moment
 *  der ehrliche Rahmen, denn eine Umleitung zur Anmeldung ist bereits im Gang.
 *  (Eine VERALTETE gespeicherte Anmeldung zeigt das Skelett bewusst weiter:
 *  meistens ist die Sitzung gueltig, und der Boot-Abgleich ueber /auth/me —
 *  antwortet ohne Sitzung mit 200 {user:null} — raeumt den Irrtum nach genau
 *  einer Serverrunde weg. Schneller geht es nicht, ohne jede gueltige Sitzung
 *  um dieselbe Runde zu bremsen.)
 *
 *  Als Huelle um das eigentliche Drahtgitter gebaut, nicht als fruehes return
 *  darin: das Innere traegt weitere Hooks, und ein return zwischen Hooks ist
 *  React-Fehler #310 — die Route stirbt zur Laufzeit, waehrend die Typpruefung
 *  gruen bleibt (schon einmal passiert, siehe Erinnerung react-hook-nach-return). */
export function SeitenSkelett() {
  const angemeldet = useStore((s) => s.auth !== null);
  if (!angemeldet) return null;
  return <SeitenSkelettInnen />;
}

function SeitenSkelettInnen() {
  const { mass, filterAktiv, railDa, tabListe, istAdmin, de } = useDashMass();
  /** gemessener Wert, sonst der einkodierte Rueckfall (CSS-Klassen bleiben) */
  const h = (k: "rail" | "kopf" | "suche" | "filter" | "hero" | "tabs"): number | undefined => (mass ? mass[k] : undefined);
  /* Gemessen wird die EIGENE Hoehe des Blocks. Die Abstaende dazwischen stehen
     hier im Skelett (es baut dieselbe Abfolge nach) und bleiben unangetastet —
     sonst wuerde der Abstand einmal gemessen und einmal gezeichnet und der
     Block waere um genau diesen Betrag zu hoch. */
  const blockStil = (k: "rail" | "kopf" | "suche" | "filter" | "hero" | "tabs") => (h(k) ? { height: h(k) } : undefined);

  /* Kopfknoepfe: „Alle Projekte" gibt es NUR fuer globale Admins
     (ProjectStats: auth?.role === "admin"). Das steht im persistierten Store
     und ist ohne jede Serverrunde lesbar — der Phantomknopf 130x40 der
     Vorgaengerfassung war schlicht ungefragte Information. */
  const kopfKnoepfe = istAdmin ? [de ? "Alle Projekte" : "All projects", de ? "Karte" : "Map"] : [de ? "Karte" : "Map"];
  /* Filterchips: welche es gibt, haengt am Projekt (Ortsteilfeld, Kategorie-
     feld, Bewertungsfeld) und ist beim Zeichnen unbekannt. Ohne Messung
     lieber ZWEI als vier — gemessen kostete jede zu viel gezeichnete Pille
     rund 7 000 px² Fehlflaeche, eine fehlende dagegen nur ihre eigene Tinte. */
  const chips = mass?.chips ?? [0, 0];
  const chips2 = mass?.chips2 ?? [0, 0];
  const heroN = mass?.heroLab?.length ?? 3;
  const tabW = mass?.tabW ?? [0, 0, 0];
  const tabTexte = [de ? "Diagramme" : "Charts", de ? "Objektliste" : "Object list", de ? "Mängel" : "Defects"];

  return (
    <div className={`anim-fade pad-responsive${filterAktiv ? " skl-aktiv" : ""}`} style={{ maxWidth: 1180, margin: "0 auto", padding: "26px 28px 60px" }} aria-busy>
      <style>{SKL_CSS}</style>
      {/* Projektschiene — nur auf dem Telefon sichtbar (wie psw-rail);
          Anzeige NUR ueber die Klasse, sonst schlaegt inline die Media-Regel */}
      {railDa && (
        <div className="psw-rail skl-rail" style={blockStil("rail")}>
          {[96, 92, 84].map((w, i) => (
            <div key={i} className="skeleton-row" style={{ height: 34, width: w, borderRadius: 17 }} />
          ))}
        </div>
      )}
      {/* Kopfzeile: die Hoehe (42) kommt aus dem echten <h1> — 28 px Schrift
          mal der geerbten Zeilenhoehe 1,5. Nichts abgeschrieben. */}
      <div
        className="skl-kopf"
        style={{ display: "flex", flexWrap: "wrap", alignItems: "center", alignContent: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 20, overflow: "hidden", ...blockStil("kopf") }}
      >
        {/* Dieselbe Verschachtelung wie im Original (ProjectStats: Titel und
            StatusBadge in EINER Gruppe, daneben die Knoepfe). Ohne den Chip war
            die linke Gruppe rund 80 px schmaler — auf dem Telefon passte damit
            alles in EINE Zeile, waehrend der Inhalt in ZWEI umbricht. Das Kaestchen
            war dann richtig hoch und innen halb leer. */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
          <h1 style={{ fontSize: 28, minWidth: 0 }}>
            <Tb txt="Musterprojekt Nord" w={mass?.titel} style={{ fontSize: 28, lineHeight: 1.2, borderRadius: 10 }} />
          </h1>
          <Knopf className="btn btn-ghost" style={{ borderRadius: 999, padding: "4px 12px" }}>
            <Tb txt="Aktiv" style={{ fontSize: 12.5, lineHeight: 1.2 }} />
          </Knopf>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {kopfKnoepfe.map((txt, i) => (
            <Knopf key={i} className="btn btn-ghost" style={mass?.kopfBtn?.[i] ? { width: mass.kopfBtn[i] } : undefined}>
              <Ib s={17} />
              <Tb txt={txt} style={{ fontSize: 14, lineHeight: 1.2 }} />
            </Knopf>
          ))}
        </div>
      </div>
      {/* Kommandoleiste. Sie ist im Original ein GEFUELLTER Knopf mit Rand und
          Schatten — die Tinte darin sind Symbol, Platzhalterzeile und die
          F-Taste. Die Vorgaengerin malte stattdessen einen einzigen
          schimmernden Block ueber die volle Breite: nach den Diagrammen der
          groesste Einzelfehler der Messung (149 Phantomzellen in einem Band). */}
      <div
        className="skl-suche"
        style={{ display: "flex", alignItems: "center", gap: 14, width: "100%", height: 62, padding: "0 22px", borderRadius: 21, border: "1px solid var(--glass-border)", background: "var(--panel-solid)", boxShadow: "var(--shadow)", marginBottom: 14, overflow: "hidden", ...blockStil("suche") }}
      >
        <Ib s={21} r={7} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <Tb txt={de ? SUCH_PLATZHALTER.de : SUCH_PLATZHALTER.en} style={{ fontSize: 15.5, lineHeight: 1.1 }} />
        </span>
        {/* die F-Taste: Umriss wie das echte <kbd>, Tinte nur der Buchstabe */}
        <span style={{ flex: "none", padding: "3px 10px", borderRadius: 8, border: "1px solid var(--glass-border)", background: "var(--field)" }}>
          <Tb txt="F" style={{ fontSize: 11.5, lineHeight: 1.3, borderRadius: 3 }} />
        </span>
      </div>
      {/* Filterleiste — dieselbe Karte und dieselbe Zweiteilung wie das
          Original (FilterBar: .card > .filterbar-row > zwei .filterbar-chips).
          Der Umbruch aufs Telefon kommt damit aus base.css und nicht mehr aus
          einer zweiten, handgepflegten Media Query hier. */}
      <div className="card skl-filter" style={{ padding: "12px 14px", marginBottom: 14, overflow: "hidden", ...blockStil("filter") }}>
        <div className="filterbar-row" style={{ display: "flex", alignItems: "flex-start", gap: 8, rowGap: 8, paddingBottom: 2 }}>
          <div className="filterbar-chips" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", rowGap: 8 }}>
            <Knopf className="filterws-open">
              <Ib s={16} />
              <Tb txt="Filter" style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".04em", lineHeight: 1.3 }} />
            </Knopf>
            {chips.map((w, i) => (
              <ChipSkelett key={i} w={w} txt={i === 0 ? (de ? "Kategorie" : "Category") : de ? "Gesamtbewertung" : "Overall rating"} />
            ))}
          </div>
          <div className="filterbar-chips filterbar-chips-2" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", rowGap: 8 }}>
            <ChipSkelett w={chips2[0] ?? 0} txt={de ? "Spalte" : "Column"} />
            <ChipSkelett w={chips2[1] ?? 0} txt={de ? "Gespeichert" : "Saved"} className="filt-lesezeichen" />
          </div>
        </div>
        {/* dritte Zeile: die aktiven Bedingungen — nur wenn ein Filter gemerkt ist */}
        <div className="skl-bedingung">
          <div className="skeleton-row" style={{ height: 30, width: 190, borderRadius: 15, flex: "none" }} />
          <div className="skeleton-row" style={{ height: 12, width: 30, borderRadius: 5, flex: "none" }} />
          <div className="skeleton-row" style={{ height: 30, width: 160, borderRadius: 15, flex: "none" }} />
        </div>
      </div>
      {/* Kennzahlkacheln — DIESELBEN Klassen wie die fertige Seite
          (.hero-grid/.hero-tile/.hero-label/.hero-value/.hero-von). Polster,
          Rasterabstand, Kachelhoehe und Schriftmasse kommen damit aus base.css.
          Die Balken stehen INNERHALB der echten Textelemente: deren Zeilenhoehe
          bestimmt die Hoehe, ihre Schrift die Breite. */}
      <div className="hero-grid" style={{ gridTemplateColumns: `repeat(${heroN}, minmax(0,1fr))`, marginBottom: 16, ...blockStil("hero") }}>
        {Array.from({ length: heroN }, (_, i) => (
          <div key={i} className={`card hero-tile${i === 0 ? " skl-kachel-akzent" : ""}`}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <Ib s={15} />
              <span className="hero-label" style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                <Tb txt={heroTexte(de)[i] ?? heroTexte(de)[0]!} w={mass?.heroLab?.[i]} style={{ borderRadius: 4 }} />
              </span>
              {/* Bedingungspille bzw. Trichter rechts — nur mit aktivem Filter */}
              <span className="skeleton-row skl-nur-aktiv" style={{ width: i === 0 ? 44 : 13, height: 13, borderRadius: 6, flex: "none" }} />
            </div>
            <div className="hero-value">
              <Tb txt="1.234" w={mass?.heroVal?.[i]} style={{ borderRadius: 6 }} />
            </div>
            <div className="hero-von skl-nur-aktiv">
              <Tb txt="von 1.234" style={{ borderRadius: 4 }} />
            </div>
          </div>
        ))}
      </div>
      {/* Panel: Reiterzeile + Inhalt. Die Reiter sind durchsichtige Knoepfe mit
          Trennlinie darunter — gefuellte Pillen waren dort 42 000 px² daneben. */}
      <div className="card" style={{ padding: "4px 22px 24px" }}>
        <div className="skl-tabs" style={{ paddingBottom: 2, marginBottom: 18, ...blockStil("tabs") }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 2, borderBottom: "1px solid var(--glass-border)" }}>
            {tabW.map((w, i) => (
              <Knopf
                key={i}
                className={(tabListe ? 1 : 0) === i ? "skl-tab-aktiv" : ""}
                style={{ position: "relative", padding: "11px 16px", fontSize: 13.5, fontWeight: 600, background: "transparent", border: "none", display: "flex", alignItems: "center", gap: 7, ...(w ? { width: w } : null) }}
              >
                <Ib s={16} />
                <Tb txt={tabTexte[i] ?? tabTexte[0]!} style={{ fontSize: 13.5, lineHeight: 1.2 }} />
              </Knopf>
            ))}
          </div>
        </div>
        {tabListe ? <ListeSkelett /> : <DiagrammSkelett mass={mass} />}
      </div>
    </div>
  );
}

/** Die drei Standard-Kennzahlen — Rueckfall, solange keine Messung vorliegt.
 *  Ein Projekt kann andere konfigurieren; dann korrigiert die erste Messung. */
function heroTexte(de: boolean): string[] {
  return de ? ["Objekte", "Gesamtfläche · stadtweit", "Ortsteile"] : ["Features", "Total area · citywide", "Districts"];
}

/**
 * Filterchip als Umriss.
 *
 * Das Original (shared.tsx, chipStyle) ist ein Knopf mit 1 px Rand, ruhigem
 * Grund und 8/13 px Polster; die Tinte darin sind Symbol und Beschriftung.
 * Die Vorgaengerin fuellte stattdessen das ganze Rechteck — bei fuenf Chips
 * das Vielfache der echten Tinte, plus zwei Chips, die es gar nicht gab.
 */
function ChipSkelett({ w, txt, className = "" }: { w: number; txt: string; className?: string }) {
  return (
    <Knopf
      className={`filt-chip ${className}`}
      style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 13px", borderRadius: 11, border: "1px solid var(--field-border)", background: "var(--field)", flex: "none", ...(w ? { width: w } : null) }}
    >
      <Ib s={16} />
      <Tb txt={txt} style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.2 }} />
    </Knopf>
  );
}

/** Karten-Silhouette fuer /karte — am echten UI vermessen (1440 px):
 *  linkes Panel 12/12/12 x 296 (Kopf 81, Ebenenzeile 42, Ebenenkarte, unten
 *  Kartenthema 110 + Weitere Ebenen 47 + Fusszeile 59), Suchleiste mittig
 *  340x44 bei top 20, Werkzeugsaeule x308: 2 Gruppen 52x181 + 1x 52x52,
 *  Einstellungen oben rechts, Ebenen-Knopf unten rechts. Steht in der URL
 *  panel/feature, ist auch das rechte Datenpanel (360) vorgezeichnet. */
export function KartenSkelett() {
  const q = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const rechtsAuf = !!q && (!!q.get("panel") || !!q.get("feature"));
  const werkzeug = { width: 52, borderRadius: 13, display: "flex", flexDirection: "column" as const, alignItems: "center", justifyContent: "space-evenly" };
  return (
    <div aria-busy style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
      <style>{SKL_CSS}</style>
      {/* Kartenflaeche: ein ruhiger Grundton, KEIN schimmerndes Riesenrechteck */}
      <div style={{ position: "absolute", inset: 0, background: "color-mix(in srgb, var(--text-mute) 7%, transparent)" }} />
      {/* linkes Ebenen-Panel (Desktop) */}
      <div className="karten-skelett-links frost" style={{ position: "absolute", left: 12, top: 12, bottom: 12, width: 296, borderRadius: 18, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Projektkopf 81 */}
        <div style={{ height: 81, borderBottom: "1px solid var(--glass-border)", display: "flex", flexDirection: "column", justifyContent: "center", gap: 7, padding: "0 14px 0 70px" }}>
          <div className="skeleton-row" style={{ height: 16, width: 130, borderRadius: 6 }} />
          <div className="skeleton-row" style={{ height: 9, width: 90, borderRadius: 4 }} />
        </div>
        {/* "Ebenen 1/1" 42 */}
        <div style={{ height: 42, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px" }}>
          <div className="skeleton-row" style={{ height: 12, width: 56, borderRadius: 5 }} />
          <div className="skeleton-row" style={{ height: 10, width: 26, borderRadius: 4 }} />
        </div>
        {/* Ebenenkarte */}
        <div style={{ padding: "0 10px" }}>
          <div className="skeleton-row" style={{ height: 74, borderRadius: 14 }} />
        </div>
        <div style={{ flex: 1 }} />
        {/* Kartenthema 110 */}
        <div style={{ height: 110, borderTop: "1px solid var(--glass-border)", padding: "10px 14px 8px", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div className="skeleton-row" style={{ height: 12, width: 92, borderRadius: 5 }} />
            <div className="skeleton-row" style={{ height: 20, width: 20, borderRadius: 7 }} />
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[70, 132, 92].map((w, i) => (
              <div key={i} className="skeleton-row" style={{ height: 25, width: w, borderRadius: 9 }} />
            ))}
          </div>
        </div>
        {/* Weitere Ebenen 47 */}
        <div style={{ height: 47, borderTop: "1px solid var(--glass-border)", display: "flex", alignItems: "center", padding: "0 14px" }}>
          <div className="skeleton-row" style={{ height: 30, width: "100%", borderRadius: 11 }} />
        </div>
        {/* Fusszeile 59: Zoom + Filter + Exportieren */}
        <div style={{ height: 59, borderTop: "1px solid var(--glass-border)", display: "flex", gap: 8, alignItems: "center", padding: "0 12px" }}>
          <div className="skeleton-row" style={{ height: 34, width: 34, borderRadius: 10, flex: "none" }} />
          <div className="skeleton-row" style={{ height: 34, flex: 1, borderRadius: 10 }} />
          <div className="skeleton-row" style={{ height: 34, flex: 1, borderRadius: 10 }} />
        </div>
      </div>
      {/* Suchleiste oben mittig — 340x44 bei top 20 (auf dem Telefon volle Breite) */}
      <div className="skeleton-row karten-skelett-suche" style={{ position: "absolute", top: 20, left: "50%", transform: "translateX(-50%)", width: "min(340px, calc(100vw - 32px))", height: 44, borderRadius: 22 }} />
      {/* Werkzeugsaeule: 2 Gruppen (52x181) + Vollbild (52x52) bei x308 */}
      <div className="karten-skelett-links frost" style={{ ...werkzeug, position: "absolute", left: 308, top: 72, height: 181 }}>
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="skeleton-row" style={{ width: 34, height: 34, borderRadius: 10 }} />
        ))}
      </div>
      <div className="karten-skelett-links frost" style={{ ...werkzeug, position: "absolute", left: 308, top: 261, height: 181 }}>
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="skeleton-row" style={{ width: 34, height: 34, borderRadius: 10 }} />
        ))}
      </div>
      <div className="karten-skelett-links frost" style={{ ...werkzeug, position: "absolute", left: 308, top: 450, height: 52 }}>
        <div className="skeleton-row" style={{ width: 34, height: 34, borderRadius: 10 }} />
      </div>
      {/* Einstellungen oben rechts, Ebenen-Knopf unten rechts */}
      <div className="frost" style={{ position: "absolute", right: rechtsAuf ? 384 : 16, top: 14, width: 44, height: 44, borderRadius: 13, display: "grid", placeItems: "center" }}>
        <div className="skeleton-row" style={{ width: 26, height: 26, borderRadius: 8 }} />
      </div>
      <div className="frost" style={{ position: "absolute", right: rechtsAuf ? 384 : 20, bottom: 18, width: 44, height: 44, borderRadius: 13, display: "grid", placeItems: "center" }}>
        <div className="skeleton-row" style={{ width: 26, height: 26, borderRadius: 8 }} />
      </div>
      {/* rechtes Datenpanel, wenn die URL eines mitbringt (panel/feature) */}
      {rechtsAuf && (
        <div className="frost" style={{ position: "absolute", right: 12, top: 12, bottom: 12, width: 360, maxWidth: "calc(100vw - 24px)", borderRadius: 18, padding: 16, display: "flex", flexDirection: "column", gap: 12, overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div className="skeleton-row" style={{ height: 18, width: 150, borderRadius: 6 }} />
            <div className="skeleton-row" style={{ height: 32, width: 92, borderRadius: 11 }} />
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {[86, 66, 72].map((w, i) => (
              <div key={i} className="skeleton-row" style={{ height: 28, width: w, borderRadius: 10 }} />
            ))}
          </div>
          <div className="skeleton-row" style={{ height: 140, borderRadius: 14 }} />
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="skeleton-row" style={{ height: 34, borderRadius: 10 }} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Uebersichtskarte (/uebersicht) — dieselbe Huelle wie /karte, aber OHNE deren
 * Moeblierung.
 *
 * Gemessen war das die schlechteste Silhouette der ganzen App (15 % Deckung):
 * MapShell gab /uebersicht dasselbe KartenSkelett wie /karte und versprach
 * damit ein 296 px breites Ebenenpanel, zwei Werkzeugsaeulen und einen
 * Ebenenknopf — nichts davon existiert dort. Was es gibt, fehlte: die Pille
 * „Übersicht — alle Kommunen" oben links, die Zoomsaeule und eine breitere
 * Suchleiste (460 statt 340).
 */
export function UebersichtSkelett() {
  return (
    <div aria-busy style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
      <style>{SKL_CSS}</style>
      <div style={{ position: "absolute", inset: 0, background: "color-mix(in srgb, var(--text-mute) 7%, transparent)" }} />
      {/* Titelpille oben links */}
      <div className="frost" style={{ position: "absolute", left: 22, top: 76, height: 36, borderRadius: 12, display: "flex", alignItems: "center", gap: 8, padding: "0 12px" }}>
        <Ib s={16} />
        <Tb txt="Übersicht — alle Kommunen" style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }} />
      </div>
      {/* Zoomsaeule links */}
      <div className="frost" style={{ position: "absolute", left: 14, top: 130, width: 52, height: 130, borderRadius: 13, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-evenly" }}>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="skeleton-row" style={{ width: 34, height: 34, borderRadius: 10 }} />
        ))}
      </div>
      {/* Suchleiste oben mittig — 460 breit, nicht 340 */}
      <div className="skeleton-row" style={{ position: "absolute", top: 20, left: "50%", transform: "translateX(-50%)", width: "min(460px, calc(100vw - 32px))", height: 44, borderRadius: 22 }} />
    </div>
  );
}

/**
 * Verwaltung (/admin) — bis heute lief sie auf den „ruhigen Rahmen", also auf
 * gar keine Silhouette: 69 % aller Bildpunkte kamen unangekuendigt, und die
 * Fusszeile stand sichtbar bei y≈700, um beim Umschalten 300 px nach unten zu
 * rutschen. Ein Element, das erscheint und wieder verschwindet, ist schlechter
 * als eine leere Flaeche.
 *
 * Dabei ist es die berechenbarste Seite des Hauses: Titelblock, Projektschiene,
 * dann eine feste Folge ZUGEKLAPPTER Karten (Card.tsx oeffnet standardmaessig
 * keine), alle gleich hoch — 24 Polster + Kopfzeile + 24 Polster = 68 px, im
 * Abstand von 20. Zwei der Reihen sind Zweispalter (Geokatalog/Ebenen,
 * Kartenansichten/Hintergrundkarten), der Rest volle Breite; unter 880 px
 * fallen auch sie in eine Spalte (admin-grid, AdminScreen.tsx).
 *
 * GRENZE: steht die Projektliste noch nicht im Zwischenspeicher, zeigt der
 * Bildschirm nach dem Chunk erst einen Spinner und die Karten danach. Der
 * Regelfall ist der Zwischenspeicher-Treffer — die Kopfleiste hat dieselbe
 * Abfrage schon gestellt.
 */
export function AdminSkelett() {
  // true = Zweispalter. Reihenfolge wie in AdminScreen.tsx.
  const reihen = [false, false, true, true, false, false, false, false, false];
  const karte = (k: number) => (
    <div key={k} className="card" style={{ height: 68, display: "flex", alignItems: "center", gap: 12, padding: "0 24px" }}>
      <Ib s={20} r={7} />
      <Tb txt="Projekte verwalten" style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2 }} />
      <span style={{ flex: 1 }} />
      <Ib s={16} r={5} />
    </div>
  );
  return (
    <div aria-busy className="anim-fade pad-responsive" style={{ maxWidth: 1180, margin: "0 auto", padding: "28px 28px 50px" }}>
      <style>{SKL_CSS}</style>
      {/* Titelblock: Symbolkachel 44, Ueberschrift 27 px, Unterzeile */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <Ib s={44} r={13} />
        <div>
          <h1 style={{ fontSize: 27 }}>
            <Tb txt="Administration" style={{ fontSize: 27, lineHeight: 1.2, borderRadius: 8 }} />
          </h1>
          <div style={{ marginTop: 3 }}>
            <Tb txt="Projekte, Nutzer, Ebenen und Formulare verwalten" style={{ lineHeight: 1.2, borderRadius: 5 }} />
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {/* Projektschiene — dieselbe Klasse wie im Betrieb, also auch dieselbe
            Sichtbarkeitsregel je Bildbreite */}
        <div className="psw-rail skl-rail" style={{ marginBottom: 0 }}>
          {[96, 92, 84].map((w, i) => (
            <div key={i} className="skeleton-row" style={{ height: 34, width: w, borderRadius: 17 }} />
          ))}
        </div>
        {reihen.map((zwei, i) =>
          zwei ? (
            <div key={i} className="skl-zweispalt">
              {karte(0)}
              {karte(1)}
            </div>
          ) : (
            karte(i)
          ),
        )}
      </div>
    </div>
  );
}
